import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  AuthPageWrapper,
  CheckboxField,
  Dropdown,
  LegalStatementsModal,
  TextField,
} from '@app/components';

import {
  academicInstitutionItems,
  academicStatusItems,
  ageTypeItems,
  dropdownOptionScheme,
  phoneTypeItems,
  PreRegistrationAcademicStatus,
  PreRegistrationUseType,
  useTypeItems,
} from './constants';

import './PreregistrationForm.scss';

const cnBem = createBemBlockBuilder(['preregistrationForm']);
const cnBemDropdown = createBemBlockBuilder(['preregistrationFormDropdown']);

export const PreregistrationForm = () => {
  const { $t } = useIntl();
  const { handleSubmit, control } = useForm({
    mode: 'onTouched',
  });
  const {
    value: isLegalStatementsModalOpen,
    toggle: toggleIsLegalStatementsModalOpen,
  } = useBoolean(false);

  const [phoneNumberType, setPhoneNumberType] = useState(phoneTypeItems[0]);
  const [ageType, setAgeType] = useState(dropdownOptionScheme);
  const [useType, setUseType] = useState(dropdownOptionScheme);
  const [academicStatus, setAcademicStatus] = useState(dropdownOptionScheme);
  const [academicInstitution, setAcademicInstitution] =
    useState(dropdownOptionScheme);
  const isAcademicInstitutionDisplayed =
    academicStatus.value &&
    academicStatus.value !== PreRegistrationAcademicStatus.highSchool;

  return (
    <AuthPageWrapper.Form
      data-auto="preregistration-form"
      onSubmit={handleSubmit(values => {
        // eslint-disable-next-line no-console
        console.log({
          ...values,
          phoneType: phoneNumberType.value,
          useType: useType.value,
          ageType: ageType.value,
          academicStatus: academicStatus.value,
          academicInstitution: academicInstitution.value,
        });
      })}
    >
      <AuthPageWrapper.FormTitle titleLevel={1}>
        {$t({
          id: 'preregistration.formTitle',
          defaultMessage: 'Preregister for a Reader Identification Card',
        })}
      </AuthPageWrapper.FormTitle>
      <AuthPageWrapper.InputGroup
        title={$t({
          id: 'preregistration.address',
          defaultMessage: 'Address',
        })}
      >
        <AuthPageWrapper.Row>
          <TextField
            name="currentAddress"
            label={$t({
              id: 'preregistration.currentAddress',
              defaultMessage: 'Current address',
            })}
            autoComplete="address-line1"
            control={control}
          />
        </AuthPageWrapper.Row>
        <AuthPageWrapper.Row>
          <TextField
            name="city"
            label={$t({
              id: 'preregistration.city',
              defaultMessage: 'City',
            })}
            autoComplete="address-level2"
            control={control}
          />
        </AuthPageWrapper.Row>
        <AuthPageWrapper.Row>
          <TextField
            name="postalCode"
            label={$t({
              id: 'preregistration.postalCode',
              defaultMessage: 'Zip/Postal code',
            })}
            control={control}
            autoComplete="postal-code"
          />
        </AuthPageWrapper.Row>
        <AuthPageWrapper.Row>
          <TextField
            name="country"
            label={$t({
              id: 'preregistration.country',
              defaultMessage: 'Country',
            })}
            control={control}
            autoComplete="country-name"
          />
        </AuthPageWrapper.Row>
      </AuthPageWrapper.InputGroup>
      <AuthPageWrapper.InputGroup
        title={$t({
          id: 'preregistration.addressOnPhotoId',
          defaultMessage: 'Address on photo ID',
        })}
      >
        <AuthPageWrapper.Row>
          <CheckboxField
            name="isSameWithCurrentAddress"
            label={$t({
              id: 'preregistration.checkboxLabel',
              defaultMessage: 'Same as current address',
            })}
            defaultValue={false}
            control={control}
          />
        </AuthPageWrapper.Row>
      </AuthPageWrapper.InputGroup>
      <AuthPageWrapper.InputGroup
        title={$t({
          id: 'preregistration.phone',
          defaultMessage: 'Phone',
        })}
      >
        <div className={cnBem('__rowInline')}>
          <Dropdown
            id="items-per-page-dropdown"
            className={cnBemDropdown('__phone')}
            label={$t({
              id: 'preregistration.type',
              defaultMessage: 'Type',
            })}
            styleType="standard"
            items={phoneTypeItems}
            selectedItem={phoneNumberType}
            onSelectedItemChange={option =>
              setPhoneNumberType(oldValue => ({
                ...oldValue,
                ...option.selectedItem,
              }))
            }
          />
          <TextField
            label={$t({
              id: 'preregistration.phoneNumber',
              defaultMessage: 'Phone number',
            })}
            name="phoneNumber"
            control={control}
            className={cnBem('__phoneInput')}
          />
        </div>
      </AuthPageWrapper.InputGroup>
      <AuthPageWrapper.InputGroup
        title={$t({
          id: 'preregistration.userType',
          defaultMessage: 'User type',
        })}
      >
        <AuthPageWrapper.Row>
          <Dropdown
            id="age-type-selection"
            label={$t({
              id: 'preregistration.age',
              defaultMessage: 'Age',
            })}
            styleType="standard"
            items={ageTypeItems}
            selectedItem={ageType}
            onSelectedItemChange={option =>
              setAgeType(oldValue => ({
                ...oldValue,
                ...option.selectedItem,
              }))
            }
          />
        </AuthPageWrapper.Row>
        <AuthPageWrapper.Row>
          <Dropdown
            id="usage-type-selection"
            label={$t({
              id: 'preregistration.use',
              defaultMessage: 'Use',
            })}
            styleType="standard"
            items={useTypeItems}
            selectedItem={useType}
            onSelectedItemChange={option => {
              setUseType(oldValue => ({
                ...oldValue,
                ...option.selectedItem,
              }));
              setAcademicStatus(dropdownOptionScheme);
              setAcademicInstitution(dropdownOptionScheme);
            }}
          />
        </AuthPageWrapper.Row>
        {useType.value === PreRegistrationUseType.academic && (
          <AuthPageWrapper.Row>
            <Dropdown
              id="academic-status-selection"
              label={$t({
                id: 'preregistration.academicStatus',
                defaultMessage: 'Academic status',
              })}
              styleType="standard"
              items={academicStatusItems}
              selectedItem={academicStatus}
              onSelectedItemChange={option =>
                setAcademicStatus(oldValue => ({
                  ...oldValue,
                  ...option.selectedItem,
                }))
              }
            />
          </AuthPageWrapper.Row>
        )}
        {isAcademicInstitutionDisplayed && (
          <AuthPageWrapper.Row>
            <Dropdown
              id="academic-institution-selection"
              label={$t({
                id: 'preregistration.academicInstitution',
                defaultMessage: 'Academic institution',
              })}
              styleType="standard"
              items={academicInstitutionItems}
              selectedItem={academicInstitution}
              onSelectedItemChange={option =>
                setAcademicInstitution(oldValue => ({
                  ...oldValue,
                  ...option.selectedItem,
                }))
              }
            />
          </AuthPageWrapper.Row>
        )}
      </AuthPageWrapper.InputGroup>
      <AuthPageWrapper.Row>
        <CheckboxField
          name="agreementLegalStatement"
          label={$t(
            {
              id: 'preregistration.agreementLegalStatement',
              defaultMessage:
                'I have read and understand the Library of Congress’ <button>Legal Statements</button>.',
            },
            {
              button: chunks => (
                <Button
                  styleType="link"
                  onClick={toggleIsLegalStatementsModalOpen}
                >
                  {chunks}
                </Button>
              ),
            }
          )}
          defaultValue={false}
          control={control}
        />
        <LegalStatementsModal
          isOpen={isLegalStatementsModalOpen}
          onClose={toggleIsLegalStatementsModalOpen}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <CheckboxField
          name="agreementReceiveEmails"
          label={$t({
            id: 'preregistration.agreementReceiveEmails',
            defaultMessage:
              'I would like to receive email communications from the Library of Congress about service requests and alerts, research information, and surveys related to doing research at the Library.',
          })}
          defaultValue={false}
          control={control}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <CheckboxField
          name="agreementReceiveUpdates"
          label={$t({
            id: 'preregistration.agreementReceiveUpdates',
            defaultMessage:
              'I would like to receive email communications from the Library of Congress about other Library programs (concerts, exhibits, lectures, etc.), customer surveys, and other services available to the general public.',
          })}
          defaultValue={false}
          control={control}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.Row>
        <CheckboxField
          name="agreementSupportEmails"
          label={$t({
            id: 'preregistration.agreementSupportEmails',
            defaultMessage:
              'I would like to receive email communications from the Library of Congress about how I might support the Library.',
          })}
          defaultValue={false}
          control={control}
        />
      </AuthPageWrapper.Row>
      <AuthPageWrapper.SubmitButton>
        {$t({
          id: 'preregistration.title',
          defaultMessage: 'Preregister',
        })}
      </AuthPageWrapper.SubmitButton>
    </AuthPageWrapper.Form>
  );
};
